import { Reservation } from "../../reservations/types/Reservation";
import { formatDateKey, isDateOnlyInRange } from "../../shared/utils/dateUtils";
import { isInternalDepartment } from "./internalDepartments";
import type { DepartmentUnavailability } from "../departmentAvailability";

export interface DailyOccupancy {
  occupied: number;
  total: number;
  percentage: number;
  checkins: number;
  checkouts: number;
  blocked: number;
  available: number;
}

interface BuildDailyOccupancyInput {
  days: Date[];
  departments: string[];
  reservations: Reservation[];
  availabilityBlocks?: DepartmentUnavailability[];
}

export const buildDailyOccupancyByDate = ({
  days,
  departments,
  reservations,
  availabilityBlocks = [],
}: BuildDailyOccupancyInput): Map<string, DailyOccupancy> => {
  const occupancyDepartments = departments.filter(
    (department) => !isInternalDepartment(department)
  );
  const visibleDepartments = new Set(occupancyDepartments);
  const occupiedByDate = new Map<string, Set<string>>();
  const checkinsByDate = new Map<string, number>();
  const checkoutsByDate = new Map<string, number>();
  const blockedByDate = new Map<string, Set<string>>();

  days.forEach((day) => {
    const dateKey = formatDateKey(day);
    occupiedByDate.set(dateKey, new Set());
    checkinsByDate.set(dateKey, 0);
    checkoutsByDate.set(dateKey, 0);
    blockedByDate.set(dateKey, new Set());
  });

  availabilityBlocks.forEach((block) => {
    if (!visibleDepartments.has(block.departamento)) return;
    days.forEach((day) => {
      if (isDateOnlyInRange(day, block.desde, block.disponible_nuevamente)) {
        blockedByDate.get(formatDateKey(day))?.add(block.departamento);
      }
    });
  });

  reservations.forEach((reservation) => {
    if (!visibleDepartments.has(reservation.departamento)) return;

    const entradaKey = formatDateKey(reservation.entrada);
    const salidaKey = formatDateKey(reservation.salida);
    if (checkinsByDate.has(entradaKey)) {
      checkinsByDate.set(entradaKey, (checkinsByDate.get(entradaKey) || 0) + 1);
    }
    if (checkoutsByDate.has(salidaKey)) {
      checkoutsByDate.set(
        salidaKey,
        (checkoutsByDate.get(salidaKey) || 0) + 1
      );
    }

    days.forEach((day) => {
      if (!isDateOnlyInRange(day, reservation.entrada, reservation.salida)) {
        return;
      }

      occupiedByDate.get(formatDateKey(day))?.add(reservation.departamento);
    });
  });

  const total = occupancyDepartments.length;
  const result = new Map<string, DailyOccupancy>();

  occupiedByDate.forEach((occupiedDepartments, dateKey) => {
    const occupied = occupiedDepartments.size;
    const blocked = blockedByDate.get(dateKey)?.size || 0;
    const available = Math.max(total - blocked, 0);
    result.set(dateKey, {
      occupied,
      total,
      blocked,
      available,
      percentage: available > 0 ? Math.round((occupied / available) * 100) : 0,
      checkins: checkinsByDate.get(dateKey) || 0,
      checkouts: checkoutsByDate.get(dateKey) || 0,
    });
  });

  return result;
};
